import {
  doc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { createShareToken } from '../../utils/shareToken';
import { normalizePriceLevel } from '../../../priceLevel';

type Place = Record<string, any> & { id: string };

const publicSnapshot = (place: Place) => ({
  placeId: place.id,
  type: place.type,
  name: place.name,
  address: place.address,
  rating: place.rating ?? null,
  description: place.description || '',
  image: place.image || '',
  images: place.images || [],
  website: place.website || '',
  parking: place.parking === true,
  degustation: place.degustation === true,
  familyFriendly: place.familyFriendly === true,
  priceLevel: place.type === 'dining' ? normalizePriceLevel(place.priceLevel) : null,
  tags: place.tags || [],
  active: true,
  createdAt: serverTimestamp(),
});

export const createPublicShare = async (place: Place, userId: string) => {
  if (!db) throw new Error('sharing/not-configured');

  const token = createShareToken();
  const batch = writeBatch(db);

  batch.set(doc(db, 'shareOwners', token), {
    uid: userId,
    placeId: place.id,
    createdAt: serverTimestamp(),
  });
  batch.set(doc(db, 'publicShares', token), publicSnapshot(place));
  await batch.commit();

  return {
    token,
    url: new URL(`/share/${token}`, window.location.origin).toString(),
  };
};

export const deletePublicShare = async (token: string) => {
  if (!db) return;

  const batch = writeBatch(db);
  batch.delete(doc(db, 'publicShares', token));
  batch.delete(doc(db, 'shareOwners', token));
  await batch.commit();
};

export const copyPublicShareUrl = async (url: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    return;
  }

  const field = document.createElement('textarea');
  field.value = url;
  field.style.position = 'fixed';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.select();
  const copied = document.execCommand('copy');
  field.remove();
  if (!copied) throw new Error('sharing/copy-failed');
};

export const openNativeShare = (
  title: string,
  text: string,
  url: string,
) => navigator.share({ title, text, url });
